import { Box, Typography } from '@mui/material';
import { Copy, Lock } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { createAdminApi, type AdminDataRecord, type AdminRecordHistory, type AdminRecordVersion } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';
import { Badge, Button, ErrorState, Icon, LoadingState } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { diffSnapshots, formatInstant, rawJson, MASKED, MISSING, type FieldChange } from './payload';
import { changeLabel, formatChangeValue, operationLabel, presentRecord, type DetailRow } from './presenters';
import { useAdminQuery } from './useAdminQuery';

/**
 * Tiroir de detail d'un enregistrement : etat courant, conflits, historique.
 *
 * Quatre regles de lecture, ecrites sur l'ecran et pas seulement ici :
 *
 * - **L'historique vient du serveur, les differences du navigateur.** Chaque
 *   evenement porte l'instantane COMPLET de l'enregistrement : la difference
 *   entre deux versions consecutives se calcule en comparant deux instantanes
 *   (`diffSnapshots`), sans que le serveur ait rien a ajouter. Seule exception,
 *   `milk_batch · update`, dont la charge est une revision partielle : elle est
 *   affichee telle quelle, jamais comparee a un instantane.
 * - **`occurredAt` est l'horloge de l'appareil**, libellee « déclaré par
 *   l'appareil » : le serveur ne l'a pas mesuree. L'appareil n'a pas de libelle
 *   (« Pixel 7 ») — l'identifiant brut est tronque, le compte auteur en dit plus.
 * - **« Opérations rejetées »** : les operations que le serveur a refusees pour
 *   conflit de version. La ligne reste ecrite meme quand l'appareil renvoie
 *   l'operation ensuite (« Conserver ma version » repart sous une cle neuve) :
 *   l'ecran ne dit donc jamais « non resolu », il dit ce que le serveur a fait.
 * - **Un champ masque est dit masque.** Le serveur ne l'a pas envoye ; l'ecran ne
 *   l'ecrit pas « — » (qui dirait vide) mais « Masqué pour votre rôle ».
 */

const copyText = async (text: string): Promise<boolean> => {
  // `try/catch` autour de l'ACCES : `navigator.clipboard` manque dans une page
  // servie en clair ou un navigateur restreint, et lire la propriete leve alors.
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    return false;
  }
};

/** L'appareil se lit par son empreinte courte (ADR 0039) : rien à tronquer. */
const deviceLabel = (hash: string | null): string => (hash === null || hash === '' ? MISSING : hash);

const FieldList = ({ rows, label }: { rows: readonly DetailRow[]; label: string }): ReactElement => (
  <Box component="dl" aria-label={label} sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, 40%) minmax(0, 1fr)' }}>
    {rows.map((row, index) => (
      <Box key={`${row.label}-${String(index)}`} sx={{ display: 'contents' }}>
        <Box component="dt" sx={{ py: 1, fontSize: 13, color: 'tokens.textMuted', borderBottom: 1, borderColor: 'tokens.border' }}>
          {row.label}
        </Box>
        <Box
          component="dd"
          sx={{
            m: 0,
            py: 1,
            fontSize: 13,
            fontWeight: 700,
            textAlign: 'right',
            overflowWrap: 'anywhere',
            borderBottom: 1,
            borderColor: 'tokens.border',
            fontFamily: row.mono === true ? fontStacks.mono : undefined,
            ...(row.masked === true ? { fontWeight: 400, fontStyle: 'italic', color: 'tokens.textMuted' } : {}),
          }}
        >
          {row.masked === true ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Icon icon={Lock} size={12} />
              {MASKED}
            </Box>
          ) : (
            row.value
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

const Heading = ({ children }: { children: string }): ReactElement => (
  <Typography component="h3" sx={{ fontSize: 15, fontWeight: 800, mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

/**
 * Differences d'une version. Un champ masque (role `support`) n'est ni « — → — »
 * ni absent : s'il est masque d'un seul cote, la ligne le dit ; masque des deux, il
 * ne se compare pas, et une version qui ne change que lui n'est jamais annoncee
 * « aucun champ modifie » (un evenement existe : quelque chose a ete ecrit).
 */
const Changes = ({ changes }: { changes: readonly FieldChange[] }): ReactElement => {
  const visible = changes.filter((change) => change.masked !== 'both');
  const hidden = changes.filter((change) => change.masked === 'both').map((change) => changeLabel(change.path));

  return (
    <Box>
      {visible.length === 0 && hidden.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>Aucun champ modifié.</Typography>
      ) : null}
      {visible.length > 0 ? (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {visible.map((change) => (
            <Typography component="li" key={change.path} sx={{ fontSize: 13 }}>
              {`${changeLabel(change.path)} : ${change.before} → ${change.after}`}
            </Typography>
          ))}
        </Box>
      ) : null}
      {hidden.length > 0 && visible.length === 0 ? (
        <Typography sx={{ fontSize: 13 }}>
          {`${hidden.length > 1 ? 'Champs masqués modifiés' : 'Champ masqué modifié'} : ${hidden.join(', ')} (${MASKED})`}
        </Typography>
      ) : null}
      {hidden.length > 0 && visible.length > 0 ? (
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
          {`${hidden.length > 1 ? 'Champs masqués' : 'Champ masqué'} pour votre rôle, non comparé${hidden.length > 1 ? 's' : ''} : ${hidden.join(', ')}.`}
        </Typography>
      ) : null}
    </Box>
  );
};

const VersionItem = ({
  type,
  version,
  previous,
  ordinal,
  latest,
}: {
  type: string;
  version: AdminRecordVersion;
  previous: AdminRecordVersion | null;
  ordinal: number;
  latest: boolean;
}): ReactElement => {
  const presentation = presentRecord(type, version.payload, version.redactedFields);
  const changes = previous === null ? null : diffSnapshots(previous.payload, version.payload, formatChangeValue, previous.redactedFields, version.redactedFields);

  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 700,
          bgcolor: latest ? 'tokens.primary' : 'tokens.neutralSoft',
          color: latest ? 'tokens.textOnPrimary' : 'tokens.textMuted',
        }}
      >
        {ordinal}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {operationLabel(version.operationType)}
          <Box component="span" sx={{ fontWeight: 400, color: 'tokens.textMuted' }}>
            {` · version serveur ${String(version.version)}`}
          </Box>
        </Typography>

        {previous === null ? (
          <Typography sx={{ fontSize: 13 }}>{presentation.summary.detail}</Typography>
        ) : changes === null ? (
          <Box>
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Charge partielle, affichée telle quelle (non comparable à la version précédente).
            </Typography>
            {presentation.rows
              .filter((row) => row.value !== MISSING)
              .map((row) => (
                <Typography key={row.label} sx={{ fontSize: 13 }}>
                  {row.label} : {row.masked === true ? MASKED : row.value}
                </Typography>
              ))}
          </Box>
        ) : (
          <Changes changes={changes} />
        )}

        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
          {`déclaré par l’appareil le ${formatInstant(version.occurredAt, true)} · appareil `}
          <Box component="span" sx={{ fontFamily: fontStacks.mono }}>
            {deviceLabel(version.originDeviceHash)}
          </Box>
          {` · ${version.actor.email ?? 'compte supprimé'}`}
        </Typography>
      </Box>
    </Box>
  );
};

const History = ({ type, history }: { type: string; history: AdminRecordHistory }): ReactElement => (
  <>
    <Heading>Historique des versions</Heading>
    {history.items.length === 0 ? (
      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>Aucune version lisible.</Typography>
    ) : (
      <Box component="ol" aria-label="Historique des versions" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.5 }}>
        {history.items.map((version, index) => (
          <VersionItem
            key={version.eventId}
            type={type}
            version={version}
            previous={index === 0 ? null : (history.items[index - 1] ?? null)}
            ordinal={(history.page - 1) * history.perPage + index + 1}
            latest={index === history.items.length - 1}
          />
        ))}
      </Box>
    )}
    {history.total > history.items.length ? (
      <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', mt: 1 }}>
        {`${String(history.total)} versions au total : les ${String(history.items.length)} premières sont affichées.`}
      </Typography>
    ) : null}
  </>
);

const Conflicts = ({ history }: { history: AdminRecordHistory }): ReactElement | null =>
  history.conflicts.length === 0 ? null : (
    <>
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Badge label="Conflit détecté" tone="warning" dot />
      </Box>
      <Heading>Opérations rejetées</Heading>
      <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', mb: 1 }}>
        Opérations refusées par le serveur pour conflit de version ; l’appareil a pu les renvoyer depuis.
      </Typography>
      <Box component="ul" aria-label="Opérations rejetées" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}>
        {history.conflicts.map((conflict, index) => (
          <Typography component="li" key={`${conflict.createdAt}-${String(index)}`} sx={{ fontSize: 13 }}>
            {`Rejeté le ${formatInstant(conflict.createdAt, true)} · opération ${operationLabel(conflict.operationType)} · version serveur ${conflict.currentVersion === null ? MISSING : String(conflict.currentVersion)}`}
          </Typography>
        ))}
      </Box>
      {history.conflictsTruncated ? (
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', mt: 0.5 }}>
          Plus de cent opérations rejetées existent : seules les cent plus récentes sont affichées.
        </Typography>
      ) : null}
    </>
  );

export interface RecordDetailProps {
  readonly babyId: string;
  readonly type: string;
  readonly entityId: string;
  readonly collectionLabel: string;
  /** Enregistrement de la page courante ; `null` après un lien direct vers un enregistrement hors page. */
  readonly record: AdminDataRecord | null;
  readonly token: number;
  readonly onRetry: () => void;
}

export const RecordDetail = ({ babyId, type, entityId, collectionLabel, record, token, onRetry }: RecordDetailProps): ReactElement => {
  const http = useHttpClient();
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const history = useAdminQuery(
    `history|${babyId}|${type}|${entityId}`,
    () => createAdminApi(http).getRecordHistory(babyId, type, entityId),
    { token },
  );

  // Hors page, l'etat courant est la derniere version recue : l'instantane le
  // plus recent que le serveur ait rangé pour cet enregistrement.
  const last = history.data === null ? null : (history.data.items[history.data.items.length - 1] ?? null);
  const shown =
    record !== null
      ? { payload: record.payload, redacted: record.redactedFields }
      : last === null
        ? null
        : { payload: last.payload, redacted: last.redactedFields };

  const presentation = shown === null ? null : presentRecord(type, shown.payload, shown.redacted);
  const when = presentation?.summary.when ?? record?.updatedAt ?? null;
  const title = `${presentation?.summary.nature?.label ?? collectionLabel} · ${formatInstant(when)}`;

  const copy = async (): Promise<void> => {
    const document =
      record ?? (last === null ? null : { entityType: type, entityId, version: last.version, lastWriter: last.actor, payload: last.payload, redactedFields: last.redactedFields });

    setCopied((await copyText(rawJson(document))) ? 'done' : 'failed');
  };

  return (
    <Box>
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 800, lineHeight: '24px' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
        {record === null
          ? `Enregistrement hors de la page affichée : dernière version lue${history.data !== null && history.data.total > history.data.items.length ? ` (la ${String(history.data.items.length)}ᵉ sur ${String(history.data.total)} : seules les premières versions sont lues)` : ''}.`
          : `Reçu par le serveur le ${formatInstant(record.updatedAt, true)} · version serveur ${String(record.version)} · ${record.lastWriter.email ?? 'compte supprimé'}`}
      </Typography>

      {history.failure !== null && history.data === null ? (
        <Box sx={{ mt: 2 }}>
          <ErrorState
            title={history.failure.notFound ? 'Aucune version lisible' : 'Le chargement a échoué'}
            message={history.failure.notFound ? 'Le journal ne contient aucune version lisible de cet enregistrement.' : history.failure.message}
            onRetry={onRetry}
          />
        </Box>
      ) : null}

      {presentation?.recognised === true ? (
        <Box sx={{ mt: 1.5 }}>
          <FieldList rows={presentation.rows} label="Champs de l’enregistrement" />
        </Box>
      ) : null}

      {presentation !== null && !presentation.recognised ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 13 }}>
            Forme de charge utile inattendue : elle est affichée telle que le serveur l’a reçue.
          </Typography>
        </Box>
      ) : null}

      {history.data === null && history.failure === null ? <LoadingState label="Chargement de l’historique…" /> : null}
      {history.data !== null ? (
        <>
          <Conflicts history={history.data} />
          <History type={type} history={history.data} />
        </>
      ) : null}

      {shown === null ? null : (
        <Box component="details" open={presentation !== null && !presentation.recognised} sx={{ mt: 2 }}>
          <Box component="summary" sx={{ cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Charge utile brute
          </Box>
          <Box
            component="pre"
            tabIndex={0}
            aria-label="Charge utile brute"
            sx={{
              mt: 1,
              p: 1.5,
              m: 0,
              maxHeight: 280,
              overflow: 'auto',
              fontSize: 12,
              fontFamily: fontStacks.mono,
              bgcolor: 'tokens.surfaceMuted',
              border: 1,
              borderColor: 'tokens.border',
              borderRadius: '10px',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {rawJson(shown.payload)}
          </Box>
        </Box>
      )}

      {shown === null ? null : (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button size="S" icon={Copy} onClick={() => void copy()}>
            Copier le JSON
          </Button>
          <Box role="status" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
            {copied === 'done' ? 'JSON copié.' : copied === 'failed' ? 'Copie impossible depuis ce navigateur.' : ''}
          </Box>
        </Box>
      )}
    </Box>
  );
};
